<template>
  <div>
    <div @click="onClick" class="wrapper">
      <UpdateIndicator
        :target-group="targetGroup"
        :group="group"
        :is-target-group="isTargetGroup"
        class="updateIndicator"
      />
      <div class="NameAndEditWrapper">
        <template v-if="!editing">
          <div class="nameWrapper">
            <v-tooltip :disabled="!showTooltip" right open-delay="400">
              <template v-slot:activator="{ on }">
                <div v-on="on" class="name">
                  <v-icon v-if="hasExpandingList">{{
                    isTargetGroup ? 'mdi-menu-up' : 'mdi-menu-down'
                  }}</v-icon>
                  {{ groupName }}
                </div>
                <span style="font-size: small">
                  {{ activeLocationCount }} / {{ locationCount }}地点</span
                >
              </template>

              <span>{{ groupName }}</span>
            </v-tooltip>
          </div>
        </template>
        <template v-if="editing">
          <div>
            <v-form ref="form" @submit.prevent>
              <v-text-field
                :rules="[rules.required]"
                v-model="value"
                @click.native.stop
                @keypress.enter="updateAndClose"
                @keydown.esc="closeEditor"
                dark
                color="white"
                single-line
                autofocus
                class="ma-0 pa-0"
              />
            </v-form>
            <div class="editorActions">
              <v-spacer />
              <AppButtonCancel @click.native.stop="closeEditor" small>
                キャンセル
              </AppButtonCancel>
              <AppButtonOk @click.native.stop="updateAndClose" small>
                適用
              </AppButtonOk>
            </div>
          </div>
        </template>
      </div>

      <template v-if="isTargetGroup">
        <template v-if="canEdit && !editing">
          <v-menu :offset-x="true">
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="$event.stopPropagation()"
                >mdi-dots-vertical</v-icon
              >
            </template>

            <v-card>
              <v-card-text class="pa-0">
                <v-layout>
                  <v-btn @click="openEditor" flat>
                    <v-icon small>edit</v-icon>
                    <span>地点グループ名を編集</span>
                  </v-btn>
                </v-layout>
                <v-layout v-if="hasExpandingList">
                  <v-btn @click="$emit('bulkAddLocation')" flat>
                    <v-icon small>add_location</v-icon>
                    <span>地点を一括追加</span>
                  </v-btn>
                </v-layout>
                <v-layout v-else>
                  <v-btn @click="onClickChangePoint" flat>
                    <v-icon small>add_location</v-icon>
                    <span>地点変更</span>
                  </v-btn>
                </v-layout>
              </v-card-text>
            </v-card>
          </v-menu>
        </template>
      </template>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { actions, getters } from '@/vuex/modules/pointGroupOperation/constants'
import LocationGroupOperation from '@/api/PointGroup/LocationGroupOperation/LocationGroupOperation'
import UpdateIndicator from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/PointGroup/UpdateIndicator'
import RouteHelper from '@/mixins/RouteHelper'
import { snackbarType } from '@/vuex/modules/ui/snackbar/constants'
import { locationGroupType } from '@/components/Advertiser/Operation/PointGroup/PointGroupOperation/constants'

export default {
  name: 'PointGroupName',
  components: {
    UpdateIndicator,
  },
  mixins: [RouteHelper],
  props: {
    group: {
      type: Object,
      default: () => {},
    },
    hasEditPermission: {
      type: Boolean,
      default: false,
    },
    canAccessToPointList: {
      type: Boolean,
      default: false,
    },
    hasExpandingList: {
      type: Boolean,
      default: false,
    },
    targetLocationCount: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      editing: false,
      value: this.group.name,
    }
  },
  computed: {
    canEdit() {
      return (
        !this.underSystemOwnerPage &&
        this.hasEditPermission &&
        this.canAccessToPointList
      )
    },
    targetGroup() {
      return this.$store.getters[getters.targetGroup]
    },
    isTargetGroup() {
      return this.group.id === this.targetGroup.id
    },
    showTooltip() {
      return !this.isTargetGroup
    },
    groupName() {
      if (this.isTargetGroup) return this.targetGroup.name
      return this.group.name
    },
    locationCount() {
      if (this.targetGroup.locationType === locationGroupType.POI_LOCATION)
        return this.group.locationCount
      if (this.isTargetGroup) return this.targetLocationCount.locationCount
      return this.group.locationCount
    },
    activeLocationCount() {
      if (this.targetGroup.locationType === locationGroupType.POI_LOCATION)
        return this.group.activeLocationCount
      if (this.isTargetGroup)
        return this.targetLocationCount.activeLocationCount
      return this.group.activeLocationCount
    },
    rules() {
      return LocationGroupOperation.validations
    },
  },
  watch: {
    groupName(newName) {
      this.value = newName
    },
    isTargetGroup(newValue) {
      if (!newValue) {
        this.closeEditor()
      }
    },
  },
  methods: {
    ...mapActions('snackbar', {
      showSnackbar: 'SHOW_SNACKBAR',
    }),
    onClick() {
      this.$emit('click')
    },
    onClickChangePoint() {
      this.$store.dispatch(actions.SET_IS_EDITING_POI_CATEGORY)
    },
    openEditor() {
      this.editing = true
    },
    closeEditor() {
      this.editing = false
      this.value = this.groupName
    },
    async updateAndClose() {
      if (!this.$refs.form.validate()) {
        return
      }
      try {
        await this.$store.dispatch(actions.EDIT_GROUP_NAME, {
          name: this.value,
        })
        await this.$store.dispatch(actions.SAVE)
        await this.$store.dispatch(actions.GET_GROUP_LIST, {
          ownerProfileId: this.$route.params.ownerProfileId,
        })
        this.$store.dispatch(actions.SET_TARGET_GROUP, {
          groupData: this.group,
        })
        this.showSnackbar({
          type: snackbarType.SUCCESS,
          message: 'グループ名を変更しました',
        })
        this.closeEditor()
      } catch (error) {
        console.error(error)
        this.$confirm('保存に失敗しました', {
          okText: '閉じる',
          onlyOk: true,
        })
      }
    },
  },
}
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 10px;
}
.NameAndEditWrapper {
  flex: 1;
  padding-right: 5px;
  overflow: hidden;
}
.nameWrapper {
  display: flex;
  align-items: center;
}
.name {
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex-basis: 220px;
}
.editButton {
  width: 20px;
  height: 20px;
}
.editorActions {
  display: flex;
}
</style>
